<template>
  <v-app>
    <v-container fluid class="all-booking-screen pa-0">
      <div class="booking-topbar">
        <v-btn class="back-btn" rounded depressed @click="goBack()">
          <v-icon left color="#00378e" size="34">mdi-chevron-left</v-icon>
          Back
        </v-btn>
        <div class="booking-room-title">{{ roomName || "MEETING ROOM" }}</div>
        <div class="view-toggle">
          <button
            type="button"
            :class="{ active: viewMode === 'D' }"
            @click="setViewMode('D')"
          >
            D
          </button>
          <button
            type="button"
            :class="{ active: viewMode === 'M' }"
            @click="setViewMode('M')"
          >
            M
          </button>
        </div>
      </div>

      <div class="booking-content">
        <div class="calendar-panel">
          <div class="calendar-toolbar">
            <button class="nav-btn" type="button" @click="movePeriod(-1)">
              <v-icon color="white" size="42">mdi-chevron-left</v-icon>
            </button>

            <div class="calendar-selects">
              <select v-model.number="selectedMonth" @change="changeMonthYear">
                <option
                  v-for="month in monthOptions"
                  :key="month.value"
                  :value="month.value"
                >
                  {{ month.text }}
                </option>
              </select>
              <select v-model.number="selectedYear" @change="changeMonthYear">
                <option v-for="year in yearOptions" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
            </div>

            <button class="nav-btn" type="button" @click="movePeriod(1)">
              <v-icon color="white" size="42">mdi-chevron-right</v-icon>
            </button>
          </div>

          <div v-if="viewMode === 'M'" class="month-calendar">
            <div class="weekday" v-for="day in weekDays" :key="day">
              {{ day }}
            </div>
            <button
              v-for="day in calendarDays"
              :key="day.key"
              class="day-cell"
              :class="{
                selected: day.isSelected,
                muted: !day.isCurrentMonth,
                today: day.isToday,
              }"
              type="button"
              @click="selectDate(day.date)"
            >
              <span>{{ day.label }}</span>
              <i v-if="hasBooking(day.date)"></i>
            </button>
          </div>

          <div v-else class="day-calendar">
            <div class="day-heading">
              <div>{{ selectedDate.format("dddd") }}</div>
              <strong>{{ selectedDate.format("DD MMM YYYY") }}</strong>
            </div>
            <div class="day-timeline">
              <div v-if="selectedBookings.length === 0" class="empty-day">
                No meeting
              </div>
              <div
                v-for="booking in selectedBookings"
                :key="booking.eventID || booking.id"
                class="timeline-card"
              >
                <div>{{ formatTimeRange(booking) }}</div>
                <strong>{{ booking.title }}</strong>
                <span>{{ booking.hostName }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="booking-list-panel">
          <div class="booking-list">
            <div v-if="selectedBookings.length === 0" class="empty-list">
              No booking
            </div>
            <div
              v-for="booking in selectedBookings"
              :key="booking.eventID || booking.id"
              class="booking-card"
            >
              <div class="booking-time">{{ formatTimeRange(booking) }}</div>
              <div class="booking-name">{{ booking.title }}</div>
              <div class="booking-host">{{ booking.hostName }}</div>
            </div>
          </div>

          <button class="book-now-btn" type="button" @click="bookNow()">
            Book Now
          </button>
        </div>
      </div>

      <v-dialog v-model="loading" persistent width="auto">
        <v-card
          class="pa-6 d-flex flex-column align-center justify-center"
          elevation="8"
          rounded="xl"
        >
          <v-progress-circular
            indeterminate
            color="indigo"
            size="70"
            width="6"
          ></v-progress-circular>
          <span class="mt-4 text-subtitle-1 font-weight-medium"
            >Loading, please wait...</span
          >
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import axios from "axios";
import dayjs from "dayjs";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

const INACTIVITY_TIMEOUT_MS = 60 * 1000;

export default {
  name: "AllBooking",
  data() {
    const today = dayjs();
    return {
      url: enurl.apiUrl,
      loading: false,
      roomName: "",
      viewMode: "M",
      currentMonth: today.startOf("month"),
      selectedDate: today,
      selectedMonth: today.month(),
      selectedYear: today.year(),
      bookings: [],
      inactivityTimer: null,
      weekDays: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      monthOptions: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ].map((text, value) => ({ text, value })),
    };
  },

  computed: {
    yearOptions() {
      const year = dayjs().year();
      return Array.from({ length: 11 }, (_, index) => year - 5 + index);
    },

    calendarDays() {
      const firstDate = this.currentMonth.startOf("month");
      const firstGridDate = firstDate.subtract(firstDate.day(), "day");

      return Array.from({ length: 42 }, (_, index) => {
        const date = firstGridDate.add(index, "day");
        return {
          key: date.format("YYYY-MM-DD"),
          date,
          label: date.date(),
          isCurrentMonth: date.month() === this.currentMonth.month(),
          isSelected: date.isSame(this.selectedDate, "day"),
          isToday: date.isSame(dayjs(), "day"),
        };
      });
    },

    selectedBookings() {
      return this.bookings
        .filter((booking) => booking.start.isSame(this.selectedDate, "day"))
        .sort((a, b) => a.start.valueOf() - b.start.valueOf());
    },
  },

  mounted() {
    window.addEventListener("keydown", this.preventPageZoomKeydown, true);
    window.addEventListener("wheel", this.preventPageZoomWheel, {
      passive: false,
      capture: true,
    });
    document.addEventListener("touchmove", this.preventPagePinchZoom, {
      passive: false,
    });
    document.addEventListener("gesturestart", this.preventPageZoomGesture);
    document.addEventListener("gesturechange", this.preventPageZoomGesture);
    ["touchstart", "mousedown", "keydown"].forEach((eventName) => {
      window.addEventListener(eventName, this.resetInactivityTimer, true);
    });
    this.resetInactivityTimer();
    this.loadRoomPad();
    this.loadBookings();
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.preventPageZoomKeydown, true);
    window.removeEventListener("wheel", this.preventPageZoomWheel, true);
    document.removeEventListener("touchmove", this.preventPagePinchZoom);
    document.removeEventListener("gesturestart", this.preventPageZoomGesture);
    document.removeEventListener("gesturechange", this.preventPageZoomGesture);
    ["touchstart", "mousedown", "keydown"].forEach((eventName) => {
      window.removeEventListener(eventName, this.resetInactivityTimer, true);
    });
    clearTimeout(this.inactivityTimer);
  },

  methods: {
    resetInactivityTimer() {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = setTimeout(() => {
        this.$router.replace("/MeetingRoomScreen");
      }, INACTIVITY_TIMEOUT_MS);
    },

    preventPageZoomKeydown(event) {
      if (
        (event.ctrlKey || event.metaKey) &&
        ["+", "-", "=", "0"].includes(event.key)
      ) {
        event.preventDefault();
      }
    },

    preventPageZoomWheel(event) {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
      }
    },

    preventPagePinchZoom(event) {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    },

    preventPageZoomGesture(event) {
      event.preventDefault();
    },

    async loadRoomPad() {
      try {
        const res = await axios.get(`${this.url}Calendar/getDataUserInPad`);
        if (res.data.status === 0) {
          this.roomName = res.data.data?.roomPadDetail?.name || "";
        }
      } catch (error) {
        if (axios.isSessionExpiredError?.(error)) return;
      }
    },

    async loadBookings() {
      this.loading = true;
      try {
        const startDate =
          this.viewMode === "M"
            ? this.currentMonth.startOf("month")
            : this.selectedDate.startOf("day");
        const endDate =
          this.viewMode === "M"
            ? this.currentMonth.add(1, "month").startOf("month")
            : this.selectedDate.add(1, "day").startOf("day");

        const res = await axios.get(
          `${this.url}Calendar/getDataBookingsByDateRange`,
          {
            params: {
              startDate: startDate.format("YYYY-MM-DD"),
              endDate: endDate.format("YYYY-MM-DD"),
            },
          }
        );

        if (res.data.status === 0) {
          this.bookings = this.getResponseList(res.data.data).map((item) =>
            this.mapBooking(item)
          );
          return;
        }

        this.bookings = [];
        Swal.fire({
          icon: "warning",
          width: 900,
          text: res.data.message || "Load booking failed",
        });
      } catch (error) {
        if (axios.isSessionExpiredError?.(error)) return;
        this.bookings = [];
        Swal.fire({
          icon: "warning",
          width: 900,
          text:
            error?.response?.data?.message ||
            error?.message ||
            "Cannot connect to server",
        });
      } finally {
        this.loading = false;
      }
    },

    getResponseList(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.listData)) return data.listData;
      if (Array.isArray(data?.ListData)) return data.ListData;
      return [];
    },

    mapBooking(item) {
      return {
        id: item.id || item.ID,
        eventID: item.eventID || item.EventID,
        roomName: item.roomName || item.RoomName || this.roomName,
        title: item.bookingName || item.BookingName || "No title",
        hostName: item.hostName || item.HostName || "-",
        start: dayjs(item.startDate || item.StartDate),
        end: dayjs(item.expireDate || item.ExpireDate),
        checkInStatus: item.checkInStatus ?? item.CheckInStatus,
        checkOutStatus: item.checkOutStatus ?? item.CheckOutStatus,
        endStatus: item.endStatus ?? item.EndStatus,
      };
    },

    hasBooking(date) {
      return this.bookings.some((booking) => booking.start.isSame(date, "day"));
    },

    selectDate(date) {
      this.selectedDate = date;
      if (!date.isSame(this.currentMonth, "month")) {
        this.currentMonth = date.startOf("month");
        this.syncSelectors();
        this.loadBookings();
      }
    },

    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.currentMonth = this.selectedDate.startOf("month");
      this.syncSelectors();
      this.loadBookings();
    },

    movePeriod(step) {
      if (this.viewMode === "M") {
        this.currentMonth = this.currentMonth.add(step, "month");
        this.selectedDate = this.currentMonth.date(1);
      } else {
        this.selectedDate = this.selectedDate.add(step, "day");
        this.currentMonth = this.selectedDate.startOf("month");
      }
      this.syncSelectors();
      this.loadBookings();
    },

    changeMonthYear() {
      this.currentMonth = dayjs()
        .year(this.selectedYear)
        .month(this.selectedMonth)
        .date(1)
        .startOf("month");
      this.selectedDate = this.currentMonth;
      this.loadBookings();
    },

    syncSelectors() {
      this.selectedMonth = this.currentMonth.month();
      this.selectedYear = this.currentMonth.year();
    },

    formatTimeRange(booking) {
      return `${booking.start.format("HH:mm")}-${booking.end.format("HH:mm")}`;
    },

    goBack() {
      this.$router.push("/MeetingRoomScreen");
    },

    bookNow() {
      this.$router.push({
        path: "/MeetingRoomScreen",
        query: {
          openBooking: "1",
          bookingDate: this.selectedDate.format("YYYY-MM-DD"),
        },
      });
    },
  },
};
</script>

<style scoped>
.all-booking-screen {
  height: 100vh;
  background: #eaf4ff url("~@/assets/backgroundroom.png");
  background-size: cover;
  background-position: left bottom;
  padding: clamp(18px, 2.4vw, 40px) 30px !important;
  color: #003f96;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.booking-topbar {
  flex: 0 0 auto;
  height: clamp(64px, 7.4vh, 78px);
  border-radius: 39px;
  background: #003f96;
  display: flex;
  align-items: center;
  position: relative;
  box-shadow: 0 5px 8px rgba(0, 63, 150, 0.22);
}

.back-btn {
  position: absolute;
  left: 18px;
  height: 52px !important;
  min-width: 114px !important;
  background: white !important;
  color: #00378e !important;
  font-size: 20px;
  text-transform: none;
  padding: 0 18px 0 10px !important;
}

.booking-room-title {
  width: 100%;
  text-align: center;
  color: white;
  font-size: clamp(28px, 2.4vw, 36px);
  font-weight: 800;
}

.view-toggle {
  position: absolute;
  right: 18px;
  width: 126px;
  height: 52px;
  border-radius: 26px;
  background: #dcecff;
  display: flex;
  padding: 4px;
}

.view-toggle button {
  flex: 1;
  border: 0;
  border-radius: 22px;
  color: #003f96;
  background: transparent;
  font-size: 18px;
  font-weight: 800;
  cursor: pointer;
}

.view-toggle button.active {
  background: white;
}

.booking-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(320px, 26vw, 380px);
  gap: 14px;
  margin-top: 22px;
  flex: 1;
  min-height: 0;
}

.calendar-panel,
.booking-list-panel {
  border-radius: 22px;
  box-shadow: 0 8px 22px rgba(0, 63, 150, 0.22);
  backdrop-filter: blur(1px);
}

.calendar-panel {
  background: linear-gradient(
    180deg,
    #01378e 0%,
    rgba(71, 110, 173, 0.7) 21.15%,
    rgba(255, 255, 255, 0.6) 100%
  );
  padding: clamp(22px, 3vh, 38px) clamp(28px, 3.4vw, 48px)
    clamp(20px, 2.6vh, 34px);
  opacity: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.booking-list-panel {
  width: auto;
  min-height: 0;
  background: linear-gradient(
    180deg,
    #01378e 0%,
    rgba(71, 110, 173, 0.7) 21.15%,
    rgba(255, 255, 255, 0.6) 100%
  );
  opacity: 1;
}

.calendar-toolbar {
  display: grid;
  grid-template-columns: 56px 1fr 56px;
  align-items: center;
  gap: 24px;
  margin-bottom: clamp(18px, 3vh, 38px);
  flex: 0 0 auto;
}

.nav-btn {
  width: 56px;
  height: 56px;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.calendar-selects {
  display: flex;
  justify-content: center;
  gap: 18px;
}

.calendar-selects select {
  height: clamp(44px, 5.2vh, 54px);
  min-width: 170px;
  border: 0;
  border-radius: 12px;
  background: white;
  color: #003f96;
  font-size: clamp(22px, 2vw, 31px);
  padding: 0 16px;
  outline: 0;
}

.month-calendar {
  display: grid;
  grid-template-columns: repeat(7, minmax(64px, 1fr));
  grid-template-rows: auto repeat(6, minmax(0, 1fr));
  row-gap: clamp(8px, 2.2vh, 28px);
  align-items: center;
  flex: 1;
  min-height: 0;
}

.weekday {
  color: white;
  text-align: center;
  font-size: clamp(18px, 1.8vw, 26px);
  font-weight: 500;
}

.day-cell {
  width: 100%;
  min-height: 0;
  height: 100%;
  border: 0;
  border-radius: 22px;
  background: transparent;
  color: #003f96;
  font-size: clamp(24px, 2.5vw, 37px);
  line-height: 1;
  position: relative;
  cursor: pointer;
}

.day-cell.muted {
  color: rgba(255, 255, 255, 0.82);
}

.day-cell.today:not(.selected) span {
  border-bottom: 4px solid #003f96;
}

.day-cell.selected {
  max-width: clamp(72px, 7vw, 100px);
  justify-self: center;
  background: #003f96;
  color: white;
}

.day-cell i {
  position: absolute;
  left: 50%;
  bottom: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  transform: translateX(-50%);
}

.day-calendar {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.day-heading {
  color: white;
  text-align: center;
  font-size: clamp(24px, 2.2vw, 32px);
}

.day-heading strong {
  display: block;
  color: #003f96;
  font-size: clamp(36px, 3.6vw, 52px);
}

.day-timeline {
  margin-top: 34px;
  display: grid;
  gap: 14px;
  overflow: hidden auto;
  min-height: 0;
}

.timeline-card,
.booking-card {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
  color: #003f96;
}

.timeline-card {
  padding: 18px 22px;
  font-size: 22px;
}

.timeline-card strong,
.timeline-card span {
  display: block;
}

.booking-list-panel {
  padding: 18px 18px 22px;
  display: flex;
  flex-direction: column;
}

.booking-list {
  min-height: 0;
  flex: 1;
  overflow: hidden auto;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.booking-card {
  min-height: 86px;
  padding: 10px 14px;
}

.booking-time {
  font-size: clamp(21px, 1.8vw, 25px);
  font-weight: 900;
  line-height: 1.1;
}

.booking-name {
  margin-top: 3px;
  font-size: 14px;
  font-weight: 700;
}

.booking-host {
  margin-top: 3px;
  font-size: 12px;
  font-weight: 500;
}

.empty-list,
.empty-day {
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  padding: 42px 10px;
}

.book-now-btn {
  height: 60px;
  margin-top: 16px;
  border: 0;
  border-radius: 30px;
  background: #003f96;
  color: white;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
}

@media only screen and (max-width: 900px) {
  .all-booking-screen {
    height: auto;
    min-height: 100vh;
    padding: 18px 16px !important;
    overflow: auto;
  }

  .booking-topbar {
    height: auto;
    min-height: 68px;
    padding: 72px 16px 14px;
    border-radius: 28px;
  }

  .back-btn {
    top: 10px;
    left: 12px;
  }

  .view-toggle {
    top: 10px;
    right: 12px;
  }

  .booking-room-title {
    font-size: 24px;
  }

  .booking-content {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .calendar-panel {
    max-width: none;
    min-height: 0;
    border-radius: 22px;
    padding: 22px 14px;
  }

  .calendar-toolbar {
    grid-template-columns: 42px 1fr 42px;
    gap: 8px;
    margin-bottom: 22px;
  }

  .calendar-selects select {
    min-width: 96px;
    height: 42px;
    font-size: 18px;
  }

  .month-calendar {
    grid-template-columns: repeat(7, minmax(34px, 1fr));
    row-gap: 10px;
  }

  .weekday {
    font-size: 14px;
  }

  .day-cell {
    min-height: 44px;
    font-size: 20px;
    border-radius: 14px;
  }

  .booking-list-panel {
    width: 100%;
    height: auto;
    min-height: 360px;
  }
}

@media only screen and (min-width: 901px) and (max-width: 1264px) {
  .all-booking-screen {
    padding-left: 22px !important;
    padding-right: 22px !important;
  }

  .booking-content {
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 12px;
    margin-top: 18px;
  }

  .calendar-toolbar {
    grid-template-columns: 48px 1fr 48px;
    gap: 14px;
  }

  .nav-btn {
    width: 48px;
    height: 48px;
  }

  .calendar-selects {
    gap: 12px;
  }

  .calendar-selects select {
    min-width: 132px;
  }

  .month-calendar {
    grid-template-columns: repeat(7, minmax(48px, 1fr));
  }

  .booking-list-panel {
    padding: 16px 14px 18px;
  }
}
</style>
